import { useCallback, useEffect, useMemo, useState } from "react";
import { useArtistScope } from "@/lib/artistContext";
import type {
  LedgerAccount,
  LedgerAccountInput,
  LedgerAccountUpdate,
  LedgerEntry,
  LedgerEntryInput,
  LedgerJournal,
} from "@/types/ledger";
import {
  createLedgerAccount,
  createLedgerEntry,
  fetchLedgerAccounts,
  fetchLedgerEntries,
  fetchLedgerJournals,
  postLedgerEntry,
  updateLedgerAccount,
} from "@/api/ledger";

export function useLedger() {
  const { selectedArtistId } = useArtistScope();
  const [accounts, setAccounts] = useState<LedgerAccount[]>([]);
  const [journals, setJournals] = useState<LedgerJournal[]>([]);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasArtist = Boolean(selectedArtistId);

  const load = useCallback(async () => {
    if (!selectedArtistId) {
      setAccounts([]);
      setJournals([]);
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [accountsPayload, journalsPayload, entriesPayload] = await Promise.all([
        fetchLedgerAccounts(),
        fetchLedgerJournals(),
        fetchLedgerEntries(undefined, 100),
      ]);
      setAccounts(Array.isArray(accountsPayload) ? accountsPayload : []);
      setJournals(Array.isArray(journalsPayload) ? journalsPayload : []);
      setEntries(Array.isArray(entriesPayload) ? entriesPayload : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load ledger data");
    } finally {
      setLoading(false);
    }
  }, [selectedArtistId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createEntry = useCallback(
    async (payload: LedgerEntryInput) => {
      try {
        await createLedgerEntry(payload);
        await load();
      } catch (err) {
        throw err instanceof Error ? err : new Error("Failed to create entry");
      }
    },
    [load],
  );

  const postEntry = useCallback(
    async (entryId: string) => {
      try {
        await postLedgerEntry(entryId);
        await load();
      } catch (err) {
        throw err instanceof Error ? err : new Error("Failed to post entry");
      }
    },
    [load],
  );

  const createAccount = useCallback(
    async (payload: LedgerAccountInput) => {
      try {
        await createLedgerAccount(payload);
        await load();
      } catch (err) {
        throw err instanceof Error ? err : new Error("Failed to create account");
      }
    },
    [load],
  );

  const updateAccount = useCallback(
    async (accountId: number, payload: LedgerAccountUpdate) => {
      try {
        await updateLedgerAccount(accountId, payload);
        await load();
      } catch (err) {
        throw err instanceof Error ? err : new Error("Failed to update account");
      }
    },
    [load],
  );

  return useMemo(
    () => ({
      accounts,
      journals,
      entries,
      loading,
      error,
      refresh: load,
      createEntry,
      createAccount,
      updateAccount,
      postEntry,
      hasArtist,
    }),
    [accounts, journals, entries, loading, error, load, createEntry, createAccount, updateAccount, postEntry, hasArtist],
  );
}
